import React, { useContext, useEffect, useState } from 'react';

import DOMPurify from 'dompurify';
import { i18n } from 'lane-shared';
import mjml2html from 'mjml-browser';
import { toDataURL } from 'qrcode';

import { AddressType } from 'lane-shared/types/AddressType';

import { renderToMjml } from '@faire/mjml-react/utils/renderToMjml';

import { AppContext } from 'lane-shared/contexts';
import {
  EmailVisitorUpdate,
  EmailVisitorUpdatePreviewV2,
} from 'lane-shared/domains/visitorManagement/emails';
import {
  ActivateEmailTemplate,
  EventStatus,
} from 'lane-shared/domains/visitorManagement/types';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

// https://viewthespace.atlassian.net/browse/TS-9635
const formatAddress = (address: AddressType | undefined): [string, string] => {
  if (!address) {
    return ['', ''];
  }

  return [
    `${address?.street1}${address?.street2 ? ` ${address?.street2}` : ''}`,
    `${address?.city}, ${address?.state} ${address?.code}`,
  ];
};

type Props = {
  channel: Partial<Channel>;
  emailTemplate: Partial<ActivateEmailTemplate> | undefined;
  visitorValidationEnabled?: boolean;
};

export const VisitorManagementEmailTemplatePreview = ({
  channel,
  emailTemplate,
  visitorValidationEnabled = false,
}: Props) => {
  const { whitelabel } = useContext(AppContext);
  const isV2EmailsEnabled = useFlag(
    FeatureFlag.EnableVisitorManagementV2Emails,
    false
  );
  const isHeroAspectRatioFixEnabled = useFlag(
    FeatureFlag.VisitorManagementEmailHeroAspectRatioFix,
    false
  );
  const [credential, setCredential] = useState({
    data: '',
    uri: '',
  });

  useEffect(() => {
    let isMounted = true;

    async function generatePreviewQrCode() {
      const data = await toDataURL('email-preview-qrcode');

      if (isMounted) {
        setCredential(prevCredential => ({
          ...prevCredential,
          data,
        }));
      }
    }

    generatePreviewQrCode();

    return () => {
      isMounted = false;
    };
  }, []);

  const building = {
    name: '[Property name]',
    addressLineOne: '[Property’s address]',
    addressLineTwo: '',
    timezone: '',

    visitorInstructions: emailTemplate?.entryInstructions || '',
    visitorPolicies: emailTemplate?.policies || '',

    visitorHeroUri: imageUrl(emailTemplate?.backgroundId),
    logoUri: imageUrl(channel.profile?.logo),

    qrEnabled: emailTemplate?.qrEnabled ?? true,
    visitorValidationEnabled,
    contactEmail: channel.profile?.email || '',
    mapUri: '#',
    heroAspectRatioFixEnabled: isHeroAspectRatioFixEnabled,
  };

  const host = {
    firstName: '[Host name]',
    lastName: '',
    email: isV2EmailsEnabled ? '[Host email]' : 'host.name@email.com',
    companyName: '[Company name]',
  };

  const visitor = {
    firstName: '[Visitor name]',
    lastName: '',
    email: 'visitor.name@email.com',
    phone: '(555) 555-5555',
    company: '[Visitor Company]',
  };

  const visitorPass = {
    startDate: '[Duration of the visit]',
    endDate: '',
    startTime: isV2EmailsEnabled ? '[Time of the visit]' : '',
    endTime: isV2EmailsEnabled ? '' : '',
    status: EventStatus.EVENT_STATUS_UPCOMING,
    notesToVisitor: '[Unique messages to your guests will display here]',
  };

  const [whiteLabelAddressLineOne, whiteLabelAddressLineTwo] = formatAddress(
    whitelabel.address
  );

  const previewWhitelabel = {
    name: isV2EmailsEnabled
      ? (whitelabel.profile.name ?? whitelabel.name)
      : '[White label name]',
    contactEmail: whitelabel.profile.email,
    addressLineOne: whiteLabelAddressLineOne,
    addressLineTwo: whiteLabelAddressLineTwo,
  };

  const params = {
    building,
    host,
    visitorPass,
    visitor,
    credential,
    whitelabel: previewWhitelabel,
  };

  const previewMjml = isV2EmailsEnabled
    ? renderToMjml(
        <EmailVisitorUpdatePreviewV2
          title="Email Preview"
          params={params}
          i18n={i18n}
        />
      )
    : renderToMjml(
        <EmailVisitorUpdate title="Email Preview" params={params} i18n={i18n} />
      );

  const previewHtml = mjml2html(previewMjml, { validationLevel: 'soft' });

  const sanitizedHtml = DOMPurify.sanitize(previewHtml.html);

  return (
    /*
      Dangerously setting html below in order to have the email preview look similar
      to what gets rendered in the email client (gmail, apple mail, etc).
      The mjml components used to build the email cannot be rendered directly in react hence the need for converting to html.
      The html has been sanitized using DOMPurify to prevent any attacks.
    */

    <div
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      data-test="emailTemplatePreview"
      style={
        isV2EmailsEnabled ? { maxWidth: '500px', margin: '0 auto' } : undefined
      }
    />
  );
};
